// src/hooks/useUserRole.ts

import { useState, useEffect } from 'react';
import { auth, db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged, User } from 'firebase/auth';

export type UserRole = 'admin' | 'staff' | 'client' | null;

interface UseUserRoleReturn {
  user: User | null;
  role: UserRole;
  loading: boolean;
  error: Error | null;
}

/**
 * Custom hook to get current authenticated user and their role
 */
export const useUserRole = (): UseUserRoleReturn => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      try {
        setLoading(true);
        setUser(authUser);

        if (authUser) {
          // Get user document from Firestore to get role
          const userDocRef = doc(db, 'users', authUser.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            setRole((userData.role as UserRole) || 'client');
          } else {
            // User document doesn't exist yet, default to client
            setRole('client');
          }
        } else {
          setRole(null);
        }
      } catch (err) {
        console.error('Error getting user role:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        setRole(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  return { user, role, loading, error };
};

/**
 * Helper function to check if user has specific role
 */
export const hasRole = (role: UserRole, requiredRole: 'admin' | 'staff' | 'client'): boolean => {
  if (!role) return false;
  
  // Admin has access to everything
  if (role === 'admin') return true;
  
  // Staff has access to staff and client features
  if (role === 'staff' && (requiredRole === 'staff' || requiredRole === 'client')) {
    return true;
  }
  
  // Direct role match
  return role === requiredRole;
};

/**
 * Helper function to check if user is admin
 */
export const isAdmin = (role: UserRole): boolean => {
  return role === 'admin';
};

/**
 * Helper function to check if user is staff or admin
 */
export const isStaffOrAdmin = (role: UserRole): boolean => {
  return role === 'admin' || role === 'staff';
};

